/* eslint-disable no-underscore-dangle */
// src/components/DataTable.js

import styled from '@emotion/styled';
import {
  Box,
  Checkbox,
  IconButton,
  makeStyles,
  Modal,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  // useTheme,
} from '@material-ui/core';
import React, { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import FilledStart from 'src/icons/FilledStar';
import FilterIcon from 'src/icons/filter';
import SortAscending from 'src/icons/SortAscending';
// import { SortAtoZ } from 'src/icons/SortAtoZ';
import SortDescending from 'src/icons/SortDescending';
// import { SortZtoA } from 'src/icons/SortZtoA';
import Star from 'src/icons/Star';
import useGenericStyles from 'src/styles/useGenericStyles';
// import { CustomButton2 } from '../widgets/buttons/CustomButton2';
// import ColumnCustomizer from './ColumnCustomizer';

interface Sort {
  field: string;
  order: string;
}
interface FilterOption {
  value: string;
  label: string;
}

interface TableProps {
  heading?: string | ReactNode;
  columns: any;
  data: any;
  page?: number;
  limit?: number;
  handleLimit?: (limit: number) => void;
  handlePage?: (page: number) => void;
  totalRecords?: number;
  loading?: boolean;
  showCheckBoxes?: boolean;
  showFavouriteButtons?: boolean;
  alwaysShowSortIcons?: boolean;
  getFilterOptions?: (column: string) => FilterOption[];
  handleSort?: (columnField: string) => void;
  sort?: Sort | any;
  handleApplyFilter?: (
    columnName: string,
    filterValue: string,
    filterName: string,
    checked: boolean
  ) => void;
  filters?: any;
  navigateUrl?: string;
  navigateToKey?: boolean;
  favourites?: Array<any>;
  handleClickFavourite?: (favId: string) => void;
  selectRowData?: boolean; // This prop if set to true will make the checkbox select whole row objects rather than only id's
  actionButtonCallback?: (values: any) => void; // This callback is fired when we click on the button in the heading, if its provided
  actionButtonLabel?: string;
  showPagination?: boolean;
  handleOnClick?: (rowData: any) => void; // This callback is fired when we click on the table row, if its provided
  customizableColumns?: boolean;
  onColumnChange?: (visibleColumns: string[]) => void;
  defaultVisibleColumns?: string[];
  storageKey?: string;
}

const CustomLink = styled(Link)({
  textDecoration: 'none',
  color: 'inherit',
  display: 'contents', // Ensures the Link behaves as a span for the TableRow
});

const useStyles = makeStyles(() => ({
  reportingTable: {
    '& .MuiTableCell-root': {
      padding: '5px 10px 5px 0px'
    },
    '& td:nth-child(1)': {
      // '& .MuiTableCell': {
      paddingLeft: '10px'
      // }
    },
    '& .MuiTableCell-head': {
      verticalAlign: 'middle',
      padding: '10px 5px 10px 0px'
    },
    '& th:nth-child(1)': {
      // '& .MuiTableCell': {
      paddingLeft: '10px'
      // }
    },
    '& th:nth-child(6)': {
      '& .MuiBox-root': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: 100,
        display: 'inline-block',
        height: '20px'
      },
    },
  },
}));
const CustomizedTable = ({
  // heading,
  columns,
  data,
  handlePage,
  handleLimit,
  page,
  limit,
  totalRecords,
  loading,
  getFilterOptions,
  showCheckBoxes,
  showFavouriteButtons,
  handleSort,
  sort,
  handleApplyFilter,
  filters,
  navigateUrl,
  navigateToKey,
  favourites,
  handleClickFavourite,
  selectRowData = false,
  alwaysShowSortIcons = false,
  // actionButtonCallback,
  // actionButtonLabel,
  showPagination = true,
  handleOnClick = () => { },
  // customizableColumns = false,
  // onColumnChange,
  defaultVisibleColumns,
  storageKey = 'table',
}: TableProps) => {

  const reportClass = useStyles();

  const [modalOpen, setModalOpen] = useState(false);
  const [currentFilterColumn, setCurrentFilterColumn] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  // const theme = useTheme();
  const [filterAnchorEl, setFilterAnchorEl] = useState(null); // State to track the position of the filter icon
  const [filterOptions, setFilterOptions] = useState([]); // State to hold filter options for the current column

  const classes = useGenericStyles();

  const [visibleColumns] = useState<string[]>(
    defaultVisibleColumns || columns.map(col => col.field)
  );

  // const handleColumnChange = (newVisibleColumns: string[]) => {
  //   setVisibleColumns(newVisibleColumns);
  //   if (onColumnChange) {
  //     onColumnChange(newVisibleColumns);
  //   }
  // };

  const filteredColumns = columns
    .slice()
    .sort((a, b) => {
      const savedOrder = localStorage.getItem(`${storageKey}ColumnOrder`);
      if (!savedOrder) return 0;

      const orderFields = JSON.parse(savedOrder);
      const aIndex = orderFields.indexOf(a.field);
      const bIndex = orderFields.indexOf(b.field);

      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    })
    .filter(col => visibleColumns.includes(col.field));

  const handleChangePage = (event: any, _page: number) => {
    handlePage(_page);
  };

  const handleChangeLimit = (event: any) => {
    handleLimit(parseInt(event.target.value, 10));
    handlePage(0);
  };

  const handleOpenFilterModal = (event, column) => {
    setCurrentFilterColumn(column.headerName);
    setFilterOptions(getFilterOptions(column.field)); // Get filter options for the column

    const anchorRect = event.currentTarget.getBoundingClientRect();
    const modalWidth = 214; // Set the modal width to 214px (the width of the filter modal)
    const windowWidth = window.innerWidth;
    const adjustedLeftPosition = anchorRect.left + modalWidth > windowWidth
      ? windowWidth - modalWidth - 20 // Adjust to stay within the viewport with some padding
      : anchorRect.left;

    setFilterAnchorEl({
      top: anchorRect.bottom + window.scrollY,
      left: adjustedLeftPosition + window.scrollX,
    });

    setModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setModalOpen(false);
    setFilterAnchorEl(null);
  };

  const renderCell = (column, row) => {
    if (column.render) {
      return column.render(row[column.field], row);
    }
    return row[column.field];
  };

  const renderSortIcon = (field) => {
    if (sort.field === field) {
      return sort.order === 'asc' ? <SortAscending /> : <SortDescending />;
    }
    return alwaysShowSortIcons ? <SortAscending style={{ opacity: 0.3 }} /> : null;
  };

  const handleSelectRow = (rowObjorId) => {
    // eslint-disable-next-line consistent-return
    setSelectedRows((prevSelectedRows) => {
      if (selectRowData) {
        const isSelected = prevSelectedRows?.find(
          (selectedRow) => selectedRow.id === rowObjorId.id
        );
        if (isSelected) {
          return prevSelectedRows?.filter(
            (selectedRow) => selectedRow.id !== rowObjorId.id
          );
        }
        return [...prevSelectedRows, rowObjorId];
      }
      // in case its selecting id's only
      setSelectedRows((prevSelected_Rows) => {
        if (prevSelected_Rows.includes(rowObjorId)) {
          return prevSelected_Rows.filter((rowId) => rowId !== rowObjorId);
        }
        return [...prevSelected_Rows, rowObjorId];
      });
    });
  };

  // Handle action button click
  // const handleActionButtonClick = () => {
  //   if (actionButtonCallback) {
  //     actionButtonCallback(selectedRows);
  //     setSelectedRows([]);
  //   }
  // };

  const isSelected = (rowIdorObj) =>
    selectRowData
      ? selectedRows?.some((selectedRow) => selectedRow.id === rowIdorObj.id)
      : selectedRows?.includes(rowIdorObj);

  const isFavourite = (id) => favourites?.includes(id);

  const handleSelectAllRows = () => {
    if (selectedRows?.length === data?.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(selectRowData ? data : data?.map((row) => row.id));
    }
  };

  const TableRowsLoader = ({
    rowsNum,
    columnsNum,
  }: {
    rowsNum: number;
    columnsNum: number;
  }) => (
    <TableBody>
      {[...Array(rowsNum)].map((index) => (
        <TableRow key={index}>
          <TableCell colSpan={columnsNum} sx={{
            padding: '2px 10px'
          }}>
            <Skeleton animation="wave" variant="text" />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );

  return (
    <Paper sx={{ borderRadius: '16px' }}>
      {/* {(heading || actionButtonLabel || customizableColumns) && (
        <Box
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            minHeight: '60px',
            display: 'flex',
            alignItems: 'center',
            padding: '18px 24px',
            justifyContent: 'space-between',
          }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography className={classes.smallHeading} color="#1F2D3D">
              {heading}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            {customizableColumns && (
              <ColumnCustomizer
                columns={columns}
                visibleColumns={visibleColumns}
                onColumnChange={handleColumnChange}
                storageKey={storageKey}
              />
            )}
            {actionButtonLabel && actionButtonCallback && (
              <CustomButton2
                disabled={selectedRows?.length < 1}
                onClick={handleActionButtonClick}
                backgroundColor={theme.palette.primary.main}>
                {actionButtonLabel}
              </CustomButton2>
            )}
          </Box>
        </Box>
      )} */}
      <TableContainer className={columns[5]?.field === "sprintAllocatedTime" ? reportClass.reportingTable : ''}>
        <Table style={{ width: '100%' }}>
          <TableHead sx={{
            textWrap: 'nowrap',
          }}>
            <TableRow>
              {showCheckBoxes && (
                <TableCell
                  sx={{
                    borderRight: '1px solid #EDF2F7',
                    padding: '4px',
                    width: '35px',
                    minWidth: '35px',
                    maxWidth: '35px',
                    textAlign: 'center'
                  }}
                >
                  <Checkbox
                    indeterminate={
                      selectedRows?.length > 0 &&
                      selectedRows?.length < data?.length
                    }
                    checked={
                      selectedRows?.length > 0 &&
                      selectedRows?.length === data?.length
                    }
                    onChange={handleSelectAllRows}
                    sx={{
                      width: '15px',
                      minWidth: '15px',
                      padding: '2px'
                    }}
                  />
                  {/* </Box> */}
                </TableCell>
              )}
              {showFavouriteButtons && (
                <TableCell
                  padding="checkbox"
                  sx={{
                    padding: '0 10px',
                    borderRight: '1px solid #EDF2F7',
                  }}>
                  <Checkbox checked checkedIcon={<FilledStart />} disabled />
                </TableCell>
              )}
              {filteredColumns.map((column) => (
                // <TableCell
                //   key={column.field}
                //   sx={{
                //     maxWidth: column.maxWidth || 'auto',
                //     width: column.width,
                //     textTransform: 'uppercase',
                //     // textWrap: 'nowrap',
                //     padding: '8px 10px',
                //   }}>
                //   <Box
                //     component="span"
                //     sx={{
                //       cursor: column.sortable ? 'pointer' : 'default',
                //       userSelect: 'none',
                //     }}
                //     {...(column.sortable && {
                //       onClick: () => handleSort(column.field),
                //     })}>
                //     {column.headerName}
                //   </Box>
                //   {column.filterable && (
                //     <IconButton
                //       onClick={(event) => handleOpenFilterModal(event, column)}
                //       sx={{
                //         padding: 0,
                //         marginLeft: '5px',
                //       }}>
                //       <FilterIcon />
                //     </IconButton>
                //   )}
                //   {column.sortable && (
                //     <IconButton
                //       disableRipple
                //       disableTouchRipple
                //       sx={{
                //         padding: '0 5px',
                //         borderRadius: '5px',
                //         cursor: 'default',
                //         '&:hover': {
                //           backgroundColor: 'transparent',
                //         },
                //       }}>
                //       {renderSortIcon(column.field)}
                //     </IconButton>
                //   )}
                // </TableCell>

                <TableCell
                  key={column.field}
                  sx={{
                    maxWidth: column.maxWidth || 'auto',
                    width: column.width,
                    textTransform: 'uppercase',
                    padding: '8px 10px',
                    textWrap: 'nowrap',
                    border: '1px solid #EDF2F7'
                  }}
                >
                  <Tooltip title={column.tooltip || column.headerName} arrow>
                    <Box
                      component="span"
                      sx={{
                        cursor: column.sortable ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}
                      {...(column.sortable && {
                        onClick: () => handleSort(column.field),
                      })}
                    >
                      {column.headerName}
                    </Box>
                  </Tooltip>
                  {column.filterable && (
                    <Tooltip title="Filter" arrow>
                      <IconButton
                        onClick={(event) => handleOpenFilterModal(event, column)}
                        sx={{
                          padding: 0,
                          marginLeft: '5px',
                        }}
                      >
                        <FilterIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {column.sortable && (
                    <Tooltip title="Sort" arrow>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSort(column.field);
                        }}
                        sx={{
                          padding: '0 5px',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'transparent' },
                        }}
                      >
                        {renderSortIcon(column.field)}
                      </IconButton>
                    </Tooltip>
                  )}

                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {loading ? (
            <TableRowsLoader
              rowsNum={10}
              columnsNum={
                filteredColumns.length +
                (showCheckBoxes ?? 0) +
                (showFavouriteButtons ?? 0)
              }
            />
          ) : (
            <TableBody>
              {data?.length > 0 ? (
                data?.map((row) => (
                  <TableRow
                    key={row.id}
                    onClick={() => handleOnClick(row)}
                    sx={{
                      cursor: handleOnClick ? 'pointer' : 'default',
                    }}
                    component={navigateUrl ? CustomLink : 'tr'}
                    to={
                      navigateUrl
                        ? `${navigateUrl}/${navigateToKey ? row.caseKey : (row?.id ?? row?._id)
                        }`
                        : undefined
                    }>
                    {showCheckBoxes && (
                      <TableCell
                        // width='18px'
                        sx={{
                          borderRight: '1px solid #EDF2F7',
                          width: '35px',
                          minWidth: '35px',
                          maxWidth: '35px',
                          padding: '4px',
                          textAlign: 'center'
                        }}
                        onClick={(event: React.MouseEvent) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                      >
                        {/* <Box
                          sx={{
                            width: '18px',
                            minWidth: '18px'
                          }}> */}
                        <Checkbox
                          checked={isSelected(selectRowData ? row : row.id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() =>
                            handleSelectRow(selectRowData ? row : row.id)
                          }
                        />
                        {/* </Box> */}
                      </TableCell>
                    )}
                    {showFavouriteButtons && (
                      <TableCell
                        padding="checkbox"
                        sx={{
                          padding: '0 10px',
                          borderRight: '1px solid #EDF2F7',
                          zIndex: '99999',
                        }}
                        onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          icon={<Star />}
                          checkedIcon={<FilledStart />}
                          checked={isFavourite(row.id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => handleClickFavourite(row.id)}
                        />
                      </TableCell>
                    )}
                    {filteredColumns.map((column) => (
                      <TableCell
                        key={column.field}
                        sx={{
                          maxWidth: column.maxWidth || 'auto',
                          width: column.width || 'auto',
                          padding: '4px 10px',
                          fontSize: '12px',
                          border: '1px solid #EDF2F7'
                        }}>
                        {renderCell(column, row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableCell
                  colSpan={
                    filteredColumns.length +
                    (showCheckBoxes ?? 0) +
                    (showFavouriteButtons ?? 0)
                  }
                  sx={{
                    textAlign: 'center',
                  }}>
                  {' '}
                  No data found
                </TableCell>
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      {showPagination && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0px 24px',
          }}>
          <Typography className={classes.description} color="#7A7A9D!important">
            {`Showing ${data?.length || 0
              } items out of ${totalRecords} results found`}
          </Typography>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100, 500, 1000]} // Adjust as necessary
            component="div"
            count={totalRecords} // totalRecords should be the total number of records from the API
            rowsPerPage={limit}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeLimit}
            labelDisplayedRows={({ count }) =>
              `Page ${page + 1} of ${Math.ceil(count / limit)}`
            }
          // Either add proper conditions or comment it for now - Sheryar
          // backIconButtonProps={{
          //   disabled: loading,
          // }}
          // nextIconButtonProps={{
          //   disabled: loading,
          // }}
          />
        </Box>
      )}
      <Modal open={modalOpen} onClose={handleCloseFilterModal}>
        <Box
          sx={{
            position: 'absolute',
            top: filterAnchorEl ? filterAnchorEl.top : '50%',
            left: filterAnchorEl ? filterAnchorEl.left : '50%',
            transform: 'translateY(0)',
            bgcolor: '#FFFFFF',
            boxShadow: 24,
            borderRadius: '8px',
            minWidth: '214px',
            maxHeight: '50vh',
            overflow: 'auto',
          }}>
          <Box
            className={classes.smallHeading}
            sx={{
              color: '#7A7A9D',
              position: 'sticky',
              top: 0,
              padding: '14px 12px',
              backgroundColor: '#FFFFFF',
              zIndex: 400,
              borderBottom: '1px solid lightgray',
            }}>
            Filter by {currentFilterColumn}
          </Box>
          <Box
            sx={{
              padding: '10px',
              color: '#425466',
            }}>
            {filterOptions.map((option) => (
              <div
                key={option.value}
                className={classes.inputLabel}
                style={{
                  marginBottom: '5px',
                  display: 'flex',
                  alignItems: 'center',
                }}>
                <Checkbox
                  checked={filters[currentFilterColumn]?.find(
                    (filterObj) => filterObj.value === option.value
                  )}
                  onChange={(e) =>
                    handleApplyFilter(
                      currentFilterColumn,
                      option.value,
                      option.label,
                      e.target.checked
                    )
                  }
                  sx={{
                    marginRight: '10px',
                  }}
                />
                {option.label}
              </div>
            ))}
          </Box>
        </Box>
      </Modal>
    </Paper>
  );
};

export default CustomizedTable;
